// AriaContext — the persistent intelligence layer for Six Figure OS.
//
// Provides:
//   - state (idle / listening / thinking / analyzing / found / warning /
//            critical / complete / speaking)
//   - context (current page + selection, for downstream ARIA prompting)
//   - invoke() — issues an ARIA command, returns { text, actions[] }
//
// Consumers use useAria() to read state + push contextual updates.
// AriaShell (mounted at the app root) reads state to render the
// AriaStateVisualizer + AriaCommandBar.
//
// Design principles applied (modern-web-design skill):
//   - Performance-first: state updates use useReducer for batched
//     re-renders; no polling; visualizer uses CSS animation not RAF.
//   - Accessibility: respects prefers-reduced-motion; ARIA state
//     changes announce via aria-live regions.
//   - Progressive enhancement: without JS the shell renders nothing;
//     with JS it becomes the persistent intelligence surface.

import React, { createContext, useCallback, useContext, useMemo, useReducer } from 'react';
import { authedFetch } from '../lib/authedFetch';
import { compactCatalog, isKnownTab, labelForTab } from '../nav/navCatalog';

const AriaCtx = createContext(null);

// ── ARIA action → executable UI action ─────────────────────────────
// Tabs are hash-routed (#tab=…) and usePersistentState listens for
// hashchange, so ARIA navigates by writing the hash — no coupling to
// the app's internal setTab, and it works from the root-mounted shell.
const TAB_LABELS = {
  follow_up: 'Follow-Up Manager', retain: 'Retention', book_of_business: 'Book of Business',
  calendar: 'Calendar', inbox: 'Inbox', ai_staff: 'AI Team', dial: 'Dialer',
  scorecard: 'Scorecard', clients: 'Clients', leads: 'Leads', home: 'Home',
  money_map: 'Money Map', health_command_center: 'Health Command Center',
};
function prettyTab(t) {
  // Prefer the shared catalog's label (single source of truth), then the
  // legacy map, then a humanized key.
  return labelForTab(t) || TAB_LABELS[t] || String(t || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
function setHashKeys(pairs) {
  try {
    const p = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    Object.entries(pairs).forEach(([k, v]) => p.set(k, v));
    window.location.hash = p.toString();
  } catch {
    window.location.hash = 'tab=' + (pairs.tab || '');
  }
}
// Map the endpoint's structured `action` into the { label, onClick }
// shape AriaCommandBar already renders. Only emits a button where it
// does something real — no fabricated affordances.
function buildActions(action, closeBar) {
  if (!action || !action.kind) return [];
  const done = () => { try { closeBar(); } catch { /* noop */ } };
  switch (action.kind) {
    case 'goto_tab':
      if (!action.target_tab || !isKnownTab(action.target_tab)) return [];
      return [{ label: `Open ${prettyTab(action.target_tab)}`,
                onClick: () => { setHashKeys({ tab: action.target_tab }); done(); } }];
    case 'open_client_profile':
      return [{ label: 'Open in Clients',
                onClick: () => { setHashKeys({ tab: 'clients' }); done(); } }];
    case 'choose_client':
      return (action.candidates || []).slice(0, 5).map((c) => ({
        label: c.name,
        onClick: () => { setHashKeys({ tab: 'clients' }); done(); },
      }));
    default:
      // draft_sms / start_dial — the response text carries the draft;
      // no fabricated navigation button.
      return [];
  }
}

const initialState = {
  state:       'idle',       // ARIA visual state
  contextPath: null,          // current tab id
  selection:   null,          // any currently-focused entity (lead/client/etc)
  lastMessage: null,          // most recent ARIA speech / narration
  commandBarOpen: false,      // Cmd+K state
  history:     [],            // recent commands, capped at 20
};

function reducer(state, action) {
  switch (action.type) {
    case 'setState':          return { ...state, state: action.value };
    case 'setContext':        return { ...state, contextPath: action.path, selection: action.selection || null };
    case 'setMessage':        return { ...state, lastMessage: action.value };
    case 'openCommandBar':    return { ...state, commandBarOpen: true, state: 'listening' };
    case 'closeCommandBar':   return { ...state, commandBarOpen: false, state: 'idle' };
    case 'pushHistory':       return { ...state, history: [action.entry, ...state.history].slice(0, 20) };
    default:                  return state;
  }
}

export function AriaProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const setState        = useCallback((value) => dispatch({ type: 'setState', value }), []);
  const setContext      = useCallback((path, selection) => dispatch({ type: 'setContext', path, selection }), []);
  const setMessage      = useCallback((value) => dispatch({ type: 'setMessage', value }), []);
  const openCommandBar  = useCallback(() => dispatch({ type: 'openCommandBar' }), []);
  const closeCommandBar = useCallback(() => dispatch({ type: 'closeCommandBar' }), []);

  // invoke — the ARIA entry point. Sends the command to the real
  // classifier (POST /api/ai/aria-command), maps the structured
  // response to { text, actions[] }, and sets ARIA's visual state.
  // ARIA never fabricates: on error it says so; ambiguous commands
  // come back as intent=unclear, not a made-up answer.
  const invoke = useCallback(async (query) => {
    const q = String(query || '').trim();
    setState('thinking');
    dispatch({ type: 'pushHistory', entry: { query: q, at: new Date().toISOString() } });

    // Send the live nav catalog so the server can only ever route to a
    // real, current tab key — no drift between client screens and the
    // classifier's vocabulary.
    const { ok, data, error } = await authedFetch('/api/ai/aria-command', {
      method: 'POST',
      body: { command: q, catalog: compactCatalog() },
    });

    if (!ok || !data) {
      setState('warning');
      const text = error === 'command_too_short'
        ? 'Give me a little more to work with.'
        : /401|auth|jwt|sign/i.test(String(error || ''))
          ? 'Your session looks expired — sign in again and I’ll pick right back up.'
          : 'I couldn’t reach the intelligence layer just now. Try that again in a moment.';
      return { text, actions: [] };
    }

    const { intent, response, action, confidence = 0.7 } = data;
    setMessage(response);
    setState(intent === 'unclear' ? 'warning' : (confidence >= 0.8 ? 'found' : 'complete'));

    // "Take me there": for a confident navigate to a KNOWN tab, jump
    // immediately and close the bar instead of making the operator click
    // a button. The isKnownTab guard means a stale/bogus key can never
    // strand the user on a route that doesn't exist. Lower-confidence or
    // client-lookup results still surface a button to confirm.
    if (intent === 'navigate' && action?.kind === 'goto_tab'
        && isKnownTab(action.target_tab) && confidence >= 0.7) {
      setHashKeys({ tab: action.target_tab });
      setState('complete');
      try { closeCommandBar(); } catch { /* noop */ }
      return { text: `Opening ${prettyTab(action.target_tab)}.`, actions: [], intent, confidence, navigated: true };
    }

    return { text: response, actions: buildActions(action, closeCommandBar), intent, confidence };
  }, [setState, setMessage, closeCommandBar]);

  // Memoize the context value so consumers reading `aria` don't
  // re-fire effects on every parent render. State fields are stable
  // via useReducer; setters via useCallback; invoke via useCallback.
  const value = useMemo(() => ({
    ...state,
    setState, setContext, setMessage,
    openCommandBar, closeCommandBar,
    invoke,
  }), [state, setState, setContext, setMessage, openCommandBar, closeCommandBar, invoke]);

  return <AriaCtx.Provider value={value}>{children}</AriaCtx.Provider>;
}

export function useAria() {
  const ctx = useContext(AriaCtx);
  if (!ctx) throw new Error('useAria must be used inside <AriaProvider>');
  return ctx;
}
